import { createFileRoute } from '@tanstack/react-router';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../features/auth/auth-context';
import {
  useDisplayFont,
  useGlassGlow,
} from '../features/decision-sheets/components/public-shell-parts';
import { PublicDocumentView, signInPath } from '../features/documents/share/public-document-view';
import '../styles/public-shell.css';

/**
 * Link chia sẻ tài liệu dự án — APM-PDSH-c.
 *
 * Nằm **ngoài** `_app` (khuôn `/d/$token`): link công khai phải mở được khi chưa đăng nhập, mà
 * `_app` đá người chưa đăng nhập về `/login`. Cùng một đường API cho hai trạng thái (`@Public()` +
 * đọc phiên nếu có); `key={status}` dựng lại trang khi trạng thái đăng nhập đổi.
 *
 * Vỏ kính của trang Sổ quyết định công khai (`public-shell`). Đĩa logo tròn ChillPM dẫn về **trang
 * đăng nhập** (người dùng yêu cầu 2026-10-14), kèm `next` quay lại đúng link này. Đã đăng nhập thì
 * logo vào thẳng app — `/login` của người đã đăng nhập chỉ dội về lại đây.
 */
export const Route = createFileRoute('/doc/$token')({ component: DocumentShareLinkPage });

function DocumentShareLinkPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const { status } = useAuth();
  useDisplayFont();
  useGlassGlow();
  const signedIn = status === 'authenticated';

  return (
    <div className="public-shell public-shell--fit text-foreground">
      <div className="public-shell__ambient" aria-hidden>
        <div className="public-shell__blob public-shell__blob--1" />
        <div className="public-shell__blob public-shell__blob--2" />
        <div className="public-shell__blob public-shell__blob--3" />
      </div>

      <nav className="public-shell__nav" aria-label={t('publicShell.navLabel')}>
        <div className="public-shell__pill public-shell__glass">
          {/* Đã đăng nhập thì `/login` dội ngay về đây — đưa thẳng vào app. */}
          <a
            href={signedIn ? '/projects' : signInPath(token)}
            className="public-shell__brand"
            aria-label={t(signedIn ? 'publicShell.openApp' : 'documentSharePage.logo')}
            title={t(signedIn ? 'publicShell.openApp' : 'documentSharePage.logo')}
          >
            <img src="/chill-pm-avatar.png" alt="" width={512} height={512} />
          </a>
          <span className="public-shell__kicker">{t('documentSharePage.eyebrow')}</span>
          {signedIn ? (
            <a href="/projects" className="public-shell__cta">
              {t('publicShell.openApp')}
            </a>
          ) : (
            <a href={signInPath(token)} className="public-shell__cta">
              {t('documentSharePage.signIn')}
            </a>
          )}
        </div>
      </nav>

      <main className="public-shell__main public-shell__main--wide">
        {status === 'restoring' ? (
          <p role="status" className="text-sm text-muted-foreground">
            {t('auth.restoringSession')}
          </p>
        ) : (
          <PublicDocumentView key={status} token={token} />
        )}
      </main>

      <footer className="public-shell__footer">
        {t('documentSharePage.poweredBy')} · <a href="/landing.html">{t('publicShell.about')}</a>
      </footer>
    </div>
  );
}
